import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNotifications } from '@/hooks/useNotifications';
import { useAuth } from '@/contexts/AuthContext';
import NotificationItem from './NotificationItem';
import { useMediaQuery } from '@/hooks/use-media-query';

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { unreadCount: notificationUnreadCount, notifications, markAsRead } = useNotifications();
  const { user } = useAuth();
  
  // We'll use a mock for chatUnreadCount since useChat might have similar issues
  const chatUnreadCount = 0;
  
  // Get the recent notifications from the notifications array directly
  const recentNotifications = notifications.slice(0, 5);
  
  const isMobile = useMediaQuery("(max-width: 768px)");
  
  const totalUnreadCount = notificationUnreadCount + chatUnreadCount;
  
  const handleItemClick = (id: string, relatedLink?: string) => {
    markAsRead(id);
    setOpen(false);
    
    if (relatedLink) {
      navigate(relatedLink);
    }
  };

  const MobileNotifications = () => (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative p-0">
          <Bell className="h-6 w-6 text-primary" />
          {totalUnreadCount > 0 && (
            <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px]">
              {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-[380px] p-0">
        <SheetHeader className="px-4 py-3 border-b">
          <SheetTitle>Notifications</SheetTitle>
        </SheetHeader>
        <div className="overflow-y-auto max-h-[calc(100vh-120px)]">
          {recentNotifications.length > 0 ? (
            recentNotifications.map(notification => (
              <div 
                key={notification.id} 
                className="border-b last:border-0 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => handleItemClick(notification.id, notification.relatedLink || notification.link)}
              >
                <NotificationItem notification={notification} />
              </div>
            ))
          ) : (
            <div className="p-4 text-center text-muted-foreground">
              <p>No notifications</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );

  const DesktopNotifications = () => (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative p-0">
          <Bell className="h-6 w-6 text-primary" />
          {totalUnreadCount > 0 && (
            <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px]">
              {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b">
          <h3 className="text-lg font-semibold">Notifications</h3>
        </div>
        <div className="max-h-[350px] overflow-y-auto">
          {recentNotifications.length > 0 ? (
            recentNotifications.map(notification => (
              <div 
                key={notification.id} 
                className="border-b last:border-0 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => handleItemClick(notification.id, notification.relatedLink || notification.link)}
              >
                <NotificationItem notification={notification} />
              </div>
            ))
          ) : (
            <div className="p-4 text-center text-muted-foreground">
              <p>No notifications</p>
            </div>
          )}
        </div>
        <div className="p-3 border-t">
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={() => {
              setOpen(false);
              navigate('/patient-area/notifications');
            }}
          >
            View All Notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
  
  return isMobile ? <MobileNotifications /> : <DesktopNotifications />;
};

export default NotificationBell;
